import React from 'react';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { MetaBadge } from './MetaIcon';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SyncIcon from '@mui/icons-material/Sync';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { dashboardTokens } from '../../Dashboard/Components/dashboardCardStyles';
import { GoogleIcon } from './GoogleIcon';
import { campaignStatusMeta, formatCurrencyOrDash, platformMeta } from './leadsMeta';
import {
  AdPlatform,
  IntegrationAccountStatus,
  PlatformMetrics,
} from '../../../services/marketingLeads';

const GOOD = '#22c55e';
const BAD = '#ef4444';
const WARN = '#f59e0b';

const metricLabel = (key: keyof PlatformMetrics['changePct']) =>
  ({
    leads: 'Leads',
    qualifiedLeads: 'Qualified Leads',
    callsReceived: 'Calls Received',
    costPerLead: 'Cost per Lead',
    amountSpent: 'Amount Spent',
  })[key];

const HIGHER_IS_BETTER: Record<keyof PlatformMetrics['changePct'], boolean> = {
  leads: true,
  qualifiedLeads: true,
  callsReceived: true,
  costPerLead: false,
  amountSpent: false,
};

interface PlatformCardProps {
  platform: AdPlatform;
  metrics: PlatformMetrics | null;
  integration: IntegrationAccountStatus | null;
  connecting: boolean;
  syncing: boolean;
  disconnecting?: boolean;
  onViewDetails: () => void;
  onConnect: () => void;
  onDisconnect?: () => void;
  onSyncNow: () => void;
  /** ISO 4217 of the connected ad account — the money rows are in it. */
  currency?: string;
  // Opens the platform's setup/health dialog for an already-connected
  // account (Meta: ad account + Page choice, health check).
  onManage?: () => void;
}

export const PlatformCard: React.FC<PlatformCardProps> = ({
  platform,
  metrics,
  integration,
  connecting,
  syncing,
  disconnecting = false,
  onViewDetails,
  onConnect,
  onDisconnect,
  onSyncNow,
  onManage,
  currency = 'GBP',
}) => {
  const meta = platformMeta[platform];
  const statusKey = metrics?.campaignStatus || 'NO_CAMPAIGNS';
  const integrationStatus = integration?.status || 'DISCONNECTED';
  const isConnected = integrationStatus === 'CONNECTED';
  // Connected-but-unusable: a token exists but no ad account / Page has been
  // chosen yet, so nothing can sync and no lead can arrive.
  const setupIncomplete = isConnected && integration?.setupComplete === false;
  // A failed sync on a live token is a warning, not a "reconnect" — only a
  // dead token (status ERROR) is. CONFIG errors are the server's fault.
  const syncWarning = isConnected && Boolean(integration?.lastSyncError);
  const syncErrorTitle = (() => {
    const err = integration?.lastSyncError || '';
    switch (integration?.lastErrorCode) {
      case 'CONFIG':
        return `Server misconfigured (not a Meta problem): ${err} — fix .env on the API server and restart, then Sync now.`;
      case 'RATE_LIMIT':
        return `Meta rate-limited the last sync: ${err} — it will retry tonight, or press Sync now later.`;
      case 'PERMISSION':
        return `Permission problem: ${err} — check the login's access to the ad account / Page.`;
      case 'TOKEN_INVALID':
        return `Token expired or revoked: ${err} — reconnect.`;
      default:
        return err || 'Last sync failed';
    }
  })();

  // Connection state and campaign state are two different things. The account
  // being connected is a fact; having campaigns with data is a separate one.
  // Showing "syncing…" for an account that is connected but has no campaign
  // data reads as a stuck operation, so they get separate chips.
  const hasCampaignData = statusKey !== 'NO_CAMPAIGNS';
  const campaignMeta = campaignStatusMeta[statusKey];

  const m = metrics || {
    leads: 0,
    qualifiedLeads: 0,
    callsReceived: 0,
    costPerLead: 0,
    amountSpent: 0,
    spendDays: 0,
    changePct: { leads: 0, qualifiedLeads: 0, callsReceived: 0, costPerLead: 0, amountSpent: 0 },
  };
  // See SummaryCards: an unsynced range and a zero-spend range are different
  // facts, and only one of them is "0".
  const hasSpend = (m.spendDays ?? 0) > 0;

  const metricEntries: { key: keyof PlatformMetrics['changePct']; value: string }[] = [
    { key: 'leads', value: String(m.leads) },
    { key: 'qualifiedLeads', value: String(m.qualifiedLeads) },
    { key: 'callsReceived', value: String(m.callsReceived) },
    { key: 'costPerLead', value: formatCurrencyOrDash(m.costPerLead, currency, hasSpend) },
    { key: 'amountSpent', value: formatCurrencyOrDash(m.amountSpent, currency, hasSpend) },
  ];

  return (
    <Box
      sx={{
        flex: '1 1 320px',
        minWidth: 300,
        bgcolor: dashboardTokens.cardBg,
        border: `1px solid ${dashboardTokens.border}`,
        borderRadius: dashboardTokens.cardRadius,
        p: 2.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          {platform === 'META' ? (
            <MetaBadge size={32} />
          ) : (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#fff',
                color: meta.color,
              }}
            >
              <GoogleIcon size={18} />
            </Box>
          )}
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{meta.label}</Typography>

          {isConnected && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label="Connected"
              size="small"
              sx={{
                bgcolor: '#22c55e22',
                color: GOOD,
                fontWeight: 700,
                height: 22,
                '& .MuiChip-icon': { color: GOOD, ml: 0.75 },
              }}
            />
          )}

          {isConnected && hasCampaignData && (
            <Chip
              label={campaignMeta.label}
              size="small"
              sx={{
                bgcolor: campaignMeta.bg,
                color: campaignMeta.color,
                fontWeight: 700,
                height: 22,
              }}
            />
          )}

          {isConnected && !hasCampaignData && (
            <Tooltip title="The account is connected. No campaign has delivered data in this date range yet.">
              <Chip
                label="No campaign data"
                size="small"
                sx={{
                  bgcolor: '#64748b22',
                  color: dashboardTokens.textSecondary,
                  fontWeight: 600,
                  height: 22,
                }}
              />
            </Tooltip>
          )}

          {setupIncomplete && (
            <Tooltip title="Connected, but no ad account / Page chosen yet — nothing can sync and no leads can arrive until you finish setup.">
              <Chip
                icon={<WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />}
                label="Setup incomplete"
                size="small"
                onClick={onManage}
                sx={{ bgcolor: '#f59e0b22', color: WARN, fontWeight: 700, height: 22, cursor: onManage ? 'pointer' : 'default', '& .MuiChip-icon': { color: WARN, ml: 0.75 } }}
              />
            </Tooltip>
          )}

          {syncWarning && !setupIncomplete && (
            <Tooltip title={syncErrorTitle}>
              <Chip
                label={integration?.lastErrorCode === 'CONFIG' ? 'Server config' : 'Last sync failed'}
                size="small"
                onClick={onManage}
                sx={{ bgcolor: '#f59e0b22', color: WARN, fontWeight: 700, height: 22, cursor: onManage ? 'pointer' : 'default' }}
              />
            </Tooltip>
          )}

          {integrationStatus === 'ERROR' && (
            <Tooltip title={syncErrorTitle || 'Token expired or revoked — reconnect required'}>
              <Chip
                label="Reconnect required"
                size="small"
                sx={{ bgcolor: '#ef444422', color: BAD, fontWeight: 700, height: 22 }}
              />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isConnected ? (
            <>
              {onManage && (
                <Tooltip title="Ad account, Page, token and health check">
                  <span>
                    <Button
                      size="small"
                      onClick={onManage}
                      disabled={disconnecting}
                      startIcon={<SettingsOutlinedIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        color: setupIncomplete ? WARN : '#94a3b8',
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: setupIncomplete ? 700 : 500,
                        minWidth: 0,
                        px: 1,
                      }}
                    >
                      {setupIncomplete ? 'Finish setup' : 'Manage'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Fetch the latest campaign data now">
                <span>
                  <Button
                    size="small"
                    onClick={onSyncNow}
                    disabled={syncing || disconnecting}
                    startIcon={<SyncIcon sx={{ fontSize: 15 }} />}
                    sx={{
                      color: '#94a3b8',
                      textTransform: 'none',
                      fontSize: 12,
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    {syncing ? 'Syncing…' : 'Sync now'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Remove the stored access token">
                <span>
                  <Button
                    size="small"
                    onClick={onDisconnect}
                    disabled={disconnecting || syncing}
                    startIcon={<LinkOffIcon sx={{ fontSize: 15 }} />}
                    sx={{
                      color: '#94a3b8',
                      textTransform: 'none',
                      fontSize: 12,
                      minWidth: 0,
                      px: 1,
                      '&:hover': { color: BAD, bgcolor: '#ef444411' },
                    }}
                  >
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={onConnect}
              disabled={connecting}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 700,
                borderColor: meta.color,
                color: meta.color,
                '&:hover': { borderColor: meta.color, bgcolor: `${meta.color}11` },
              }}
            >
              {connecting
                ? 'Connecting…'
                : integrationStatus === 'ERROR'
                  ? `Reconnect ${meta.label}`
                  : `Connect ${meta.label}`}
            </Button>
          )}

          <Box
            onClick={onViewDetails}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 13,
              fontWeight: 700,
              color: '#60a5fa',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            View Details <ArrowForwardIcon sx={{ fontSize: 15 }} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {metricEntries.map(({ key, value }) => {
          const changePct = m.changePct[key];
          // null = no previous-period baseline (see pctChange on the server).
          const hasDelta = changePct !== null && changePct !== undefined;
          const isIncrease = hasDelta && changePct >= 0;
          const isGood = isIncrease === HIGHER_IS_BETTER[key];
          const color = !hasDelta || changePct === 0 ? dashboardTokens.textMuted : isGood ? GOOD : BAD;

          return (
            <Box key={key} sx={{ minWidth: 90 }}>
              <Typography sx={{ fontSize: 11, color: dashboardTokens.textSecondary, mb: 0.5 }}>
                {metricLabel(key)}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                {value}
              </Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>
                {hasDelta ? `${isIncrease ? '↑' : '↓'} ${Math.abs(changePct)}%` : 'no prior data'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
